import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { nameof } from 'ts-simple-nameof';
import { Authorize, LoaderHandler, TenantConfiguration } from '../../core/domain/models';
import { Tenant } from '../../core/domain/models/tenant';
import { GetTenantByIdUsecase, LoginUsecase } from '../../core/usecases';
import { DisableLoading, EnableLoading } from '../base/state/base.actions';
import { BaseState } from '../base/state/base.state';
import {
  GetOrganizationUnitId,
  GetTenantById,
  GetTenantId,
  Login,
  ResetAllStates,
  SetAuthorize,
  SetTenantId,
  SetTenantConfigurations,
  SetTenantConfigLoadStatus,
  ViewCardSettings
} from './master-locations-global.actions';
import { LoaderStatus } from '../../core/domain/enums/loader-enum';
export class MasterLocationsGlobalStateModel {
  tenantId: string | undefined;
  tenant: Tenant | undefined;
  organizationUnitId: string | undefined;
  authorize: Authorize | undefined;
  connectionId: string | undefined;
  viewCardSettings : any;
  tenantConfiguration: TenantConfiguration[] | undefined;
  tenantConfigLoadStatus!: LoaderStatus;
}

const defaults = {
  connectionId: 'test_connection',
  tenantId: '981CEFC1-DECC-403E-8D0B-466E0967593C'.toLocaleLowerCase(),
  organizationUnitId: '781CEFC1-DECC-403E-8D0B-466E0967593C',
  authorize: undefined,
  tenant: undefined,
  viewCardSettings: undefined,
  tenantConfiguration: undefined,
  tenantConfigLoadStatus: LoaderStatus.Idle,
};
@State<MasterLocationsGlobalStateModel>({
  name: 'mls_masterLocationsGlobal',
  defaults,
})
@Injectable({ providedIn: 'root' })
export class MasterLocationsGlobalState extends BaseState {
  constructor(
    private loginUsecase: LoginUsecase,
    private getTenantById: GetTenantByIdUsecase
  ) {
    super();
  }

  @Selector()
  static TenantId(state: MasterLocationsGlobalStateModel) {
    return state.tenantId;
  }
  @Selector()
  static Tenant(state: MasterLocationsGlobalStateModel) {
    return state.tenant;
  }

  @Selector()
  static OrganizationUnitId(state: MasterLocationsGlobalStateModel) {
    return state.organizationUnitId;
  }

  @Selector()
  static ConnectionId(state: MasterLocationsGlobalStateModel) {
    return state.connectionId;
  }
  @Selector()
  static Authorize(state: MasterLocationsGlobalStateModel) {
    return state.authorize;
  }

  @Selector()
  static TenantConfigurations(state: MasterLocationsGlobalStateModel) {
    return state.tenantConfiguration;
  }

  @Selector()
  static TenantConfigLoadStatus(state: MasterLocationsGlobalStateModel) {
    return state.tenantConfigLoadStatus;
  }

  @Action(GetTenantId)
  getTenantId(ctx: StateContext<MasterLocationsGlobalStateModel>) {
    ctx.setState({ ...ctx.getState() });
  }

  @Action(SetTenantId)
  SetTenantId(
    ctx: StateContext<MasterLocationsGlobalStateModel>,
    { payload }: SetTenantId
  ) {
    ctx.setState({ ...ctx.getState(), tenantId: payload });
  }

  @Action(SetAuthorize)
  SetAuthorize(
    ctx: StateContext<MasterLocationsGlobalStateModel>,
    { payload }: SetAuthorize
  ) {
    ctx.setState({ ...ctx.getState(), authorize: payload });
  }

  @Action(GetOrganizationUnitId)
  getOrganizationUnitId(ctx: StateContext<MasterLocationsGlobalStateModel>) {
    ctx.setState({ ...ctx.getState() });
  }

  @Action(Login)
  login(
    ctx: StateContext<MasterLocationsGlobalStateModel>,
    { payload }: Login
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: MasterLocationsGlobalState.name,
      endPointName: nameof<MasterLocationsGlobalState>((state) => state.login),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    if (payload) {
      return this.loginUsecase.execute(payload).pipe(
        tap((response) => {
          if (response) {
            ctx.setState({ ...ctx.getState(), authorize: response });
            ctx.dispatch(new DisableLoading(loaderHandler));
          }
        }),
        catchError((err) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      ctx.dispatch(new DisableLoading(loaderHandler));
      return ctx.setState({ ...ctx.getState(), authorize: payload });
    }
  }

  @Action(GetTenantById)
  GetTenantById(
    ctx: StateContext<MasterLocationsGlobalStateModel>,
    { payload }: GetTenantById
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: MasterLocationsGlobalState.name,
      endPointName: nameof<MasterLocationsGlobalState>(
        (state) => state.GetTenantById
      ),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    if (payload) {
      return this.getTenantById.execute(payload).pipe(
        tap((response) => {
          if (response) {
            ctx.setState({ ...ctx.getState(), tenant: response });
            ctx.dispatch(new DisableLoading(loaderHandler));
          }
        }),
        catchError((err) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      ctx.dispatch(new DisableLoading(loaderHandler));
      return ctx.setState({ ...ctx.getState(), tenant: undefined });
    }
  }
  
  @Action(ViewCardSettings)
  ViewCardSettings(
    ctx: StateContext<MasterLocationsGlobalStateModel>,
    { payload }: ViewCardSettings
  ) {
    ctx.setState({ ...ctx.getState(), viewCardSettings: payload });
  } 
 @Action(ResetAllStates)
  resetAll(ctx: StateContext<MasterLocationsGlobalStateModel>) {
  ctx.setState({ ...defaults });
}

  @Action(SetTenantConfigurations)
  setTenantConfigurations(
    ctx: StateContext<MasterLocationsGlobalStateModel>,
    { payload }: SetTenantConfigurations
  ) {
    ctx.setState({ 
      ...ctx.getState(), 
      tenantConfiguration: payload,
      tenantConfigLoadStatus: payload ? LoaderStatus.Loaded : LoaderStatus.Idle
    });
  }

  @Action(SetTenantConfigLoadStatus)
  setTenantConfigLoadStatus(
    ctx: StateContext<MasterLocationsGlobalStateModel>,
    { payload }: SetTenantConfigLoadStatus
  ) {
    ctx.setState({ ...ctx.getState(), tenantConfigLoadStatus: payload });
  }

  @Selector()
  static ViewCardSettings(state: MasterLocationsGlobalStateModel) {
    return state.viewCardSettings;
  }
}
